import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { IonButton, IonSpinner } from '@ionic/angular/standalone';

export type XButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

// 'block' stays the default because the mobile apps and the login card rely on a full-width button.
export type XButtonExpand = 'block' | 'full' | 'inline';

@Component({
  selector: 'x-button',
  standalone: true,
  imports: [IonButton, IonSpinner],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './x-button.component.html',
  styleUrl: './x-button.component.scss',
})
export class XButtonComponent {
  readonly label = input<string>('');
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly form = input<string | undefined>(undefined);
  readonly disabled = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly expand = input<XButtonExpand>('block');
  readonly variant = input<XButtonVariant>('primary');
  readonly fill = input<'clear' | 'outline' | 'solid' | undefined>('solid');
  readonly color = input<string | undefined>(undefined);
  readonly size = input<'small' | 'default' | 'large' | undefined>('default');
  readonly cssClass = input<string>('');
  readonly loadingText = input<string>('');

  /** Not `(click)` on the host: that bubbles out of the inner button even while it is disabled. */
  readonly clicked = output<void>();

  protected readonly isDisabled = computed(
    () => this.disabled() || this.loading()
  );

  protected readonly ionExpand = computed(() =>
    this.expand() === 'inline' ? undefined : this.expand()
  );

  protected readonly classes = computed(() =>
    [
      'x-button',
      `x-button--${this.variant()}`,
      `x-button--${this.expand()}`,
      this.cssClass(),
    ]
      .filter((name) => name.length > 0)
      .join(' ')
  );

  protected onClick(): void {
    if (this.isDisabled()) {
      return;
    }

    this.clicked.emit();
  }
}
